import { useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, type Breed } from 'shared/@types';
import { Switch } from 'shared/components/form/switch';
import { Text } from 'shared/components/text';
import { H2 } from 'shared/components/title';

const characteristics = [
  'child_friendly',
  'energy',
  'intelligence',
  'learning_ability',
  'health',
  'grooming_needs',
  'hair_shedding',
  'barking_level',
  'aggressiveness',
] as const;

export function BreedComparisonTable({ breed, comparable }: { breed: Breed; comparable: Breed }) {
  const { t } = useTranslation('screens');
  const [onlyDifferences, setOnlyDifferences] = useState(false);
  const unavailable = t('breeds.compare_breeds.not_available');

  function formatRange(min: number, max: number, unit: string) {
    if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max < min) {
      return unavailable;
    }

    return `${min === max ? min : `${min} – ${max}`} ${unit}`;
  }

  function formatBreedGroup({ animal, breed_group }: Breed) {
    if (!breed_group) {
      return unavailable;
    }

    return animal === Animal.Dog
      ? `${breed_group} – ${t(`common:enums.fci_group.${breed_group}`)}`
      : String(breed_group);
  }

  const facts = [
    {
      key: 'breed_type',
      value1: t(`common:enums.breed_type.${breed.breed_type}`),
      value2: t(`common:enums.breed_type.${comparable.breed_type}`),
    },
    ...(breed.breed_group || comparable.breed_group
      ? [
          {
            key: breed.animal === Animal.Dog ? 'fci_group' : 'fife_category',
            value1: formatBreedGroup(breed),
            value2: formatBreedGroup(comparable),
          },
        ]
      : []),
    {
      key: 'height',
      value1: formatRange(breed.height_female_min, breed.height_male_max, 'cm'),
      value2: formatRange(comparable.height_female_min, comparable.height_male_max, 'cm'),
    },
    {
      key: 'weight',
      value1: formatRange(breed.weight_female_min, breed.weight_male_max, 'kg'),
      value2: formatRange(comparable.weight_female_min, comparable.weight_male_max, 'kg'),
    },
    {
      key: 'life_expectancy',
      value1: formatRange(
        breed.life_expectancy_min,
        breed.life_expectancy_max,
        t('breeds.compare_breeds.years'),
      ),
      value2: formatRange(
        comparable.life_expectancy_min,
        comparable.life_expectancy_max,
        t('breeds.compare_breeds.years'),
      ),
    },
  ];
  const ratings = characteristics
    .filter((key) => key !== 'barking_level' || breed.animal === Animal.Dog)
    .map((key) => {
      const rating1 = getRating(breed[key]);
      const rating2 = getRating(comparable[key]);

      return {
        key,
        value1: rating1 === null ? unavailable : `${rating1}/5`,
        value2: rating2 === null ? unavailable : `${rating2}/5`,
        rating1,
        rating2,
      };
    })
    .filter((row) => row.rating1 !== null || row.rating2 !== null);
  const visibleFacts = onlyDifferences ? facts.filter((row) => row.value1 !== row.value2) : facts;
  const visibleRatings = onlyDifferences
    ? ratings.filter((row) => row.value1 !== row.value2)
    : ratings;

  return (
    <View className={'mb-6'}>
      <View className={'mb-3 gap-3 md:flex-row md:items-center md:justify-between'}>
        <H2
          title={t('breeds.compare_breeds.comparison_table')}
          marginTop={false}
          marginBottom={false}
        />
        <View className={'flex-row items-center gap-3'}>
          <Switch
            value={onlyDifferences}
            onPress={setOnlyDifferences}
            size={'small'}
            accessibilityLabel={t('breeds.compare_breeds.only_differences')}
          />
          <Text className={'text-brand-brown-dark text-sm'}>
            {t('breeds.compare_breeds.only_differences')}
          </Text>
        </View>
      </View>

      <View className={'border-brand-brown-dark/15 overflow-hidden rounded-lg border bg-white'}>
        <View
          className={
            'bg-brand-brown-dark/5 border-brand-brown-dark/15 flex-row items-center border-b p-3'
          }
        >
          <Text className={'text-brand-brown-dark w-2/5 pr-2 text-sm font-bold md:text-base'}>
            {t('breeds.compare_breeds.characteristic')}
          </Text>
          <Text
            className={
              'text-brand-brown-dark min-w-0 flex-1 px-1 text-center text-sm font-bold md:text-base'
            }
          >
            {breed.name}
          </Text>
          <Text
            className={
              'text-brand-brown-dark min-w-0 flex-1 px-1 text-center text-sm font-bold md:text-base'
            }
          >
            {comparable.name}
          </Text>
        </View>

        {visibleFacts.map(({ key, ...row }) => (
          <ComparisonRow key={key} label={t(`breeds.compare_breeds.${key}`)} {...row} />
        ))}
        {visibleRatings.map(({ key, ...row }) => (
          <ComparisonRow key={key} label={t(`breeds.compare_breeds.${key}`)} {...row} />
        ))}
        {visibleFacts.length === 0 && visibleRatings.length === 0 && (
          <Text className={'text-brand-brown-dark/70 p-4 text-center'}>
            {t('breeds.compare_breeds.no_differences')}
          </Text>
        )}
      </View>

      <Text className={'text-brand-brown-dark/70 mt-3 text-sm'}>
        {t('breeds.compare_breeds.scale_description')}
      </Text>
    </View>
  );
}

function getRating(value: number) {
  return Number.isInteger(value) && value >= 1 && value <= 5 ? value : null;
}

function ComparisonRow({
  label,
  value1,
  value2,
  rating1,
  rating2,
}: {
  label: string;
  value1: string;
  value2: string;
  rating1?: number | null;
  rating2?: number | null;
}) {
  return (
    <View
      className={'border-brand-brown-dark/15 flex-row items-center border-b p-3 last:border-b-0'}
    >
      <Text className={'text-brand-brown-dark w-2/5 pr-2 text-sm md:text-base'}>{label}</Text>
      <ComparisonValue value={value1} rating={rating1} />
      <ComparisonValue value={value2} rating={rating2} />
    </View>
  );
}

function ComparisonValue({ value, rating }: { value: string; rating?: number | null }) {
  return (
    <View className={'min-w-0 flex-1 items-center px-1'}>
      <Text className={'text-brand-brown-dark text-center text-sm tabular-nums md:text-base'}>
        {rating ?? value}
        {rating != null && <Text className={'text-sm text-stone-500 md:text-base'}>/5</Text>}
      </Text>
      {rating != null && (
        <View className={'w-18 gap-0.75 mt-1.5 max-w-full flex-row'} aria-hidden={true}>
          {[1, 2, 3, 4, 5].map((step) => (
            <View
              key={step}
              className={`h-0.75 min-w-0 flex-1 rounded-sm ${step <= rating ? 'bg-brand-brown-dark' : 'bg-stone-200'}`}
            />
          ))}
        </View>
      )}
    </View>
  );
}
